import { Component, Input } from '@angular/core';
import { IResultsGuided } from 'src/app/interfaces/iresults-guided';
import { IPathwayQuizResult } from 'src/app/interfaces/ipathway-quiz-result';
import { IComplianceScoring } from 'src/app/interfaces/icompliance-scoring';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { AppConstants } from 'src/app/app-constants';
import { LocaleService } from 'src/app/services/locale.service';
import { formatList, JAPANESE_LANGUAGE } from 'src/app/utils/japanese-punctuation';

@Component({
  host: {
    class: 'theme-light',
  },
  selector: 'app-pathway-guided-result',
  standalone: false,
  templateUrl: './pathway-guided-result.component.html',
  styleUrls: ['./pathway-guided-result.component.scss'],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'results',
    },
  ],
})
export class PathwayGuidedResultComponent {
  @Input() resultReady: boolean = false;
  @Input() translationLoaded: boolean = false;
  @Input() guidedResults: (IResultsGuided | IPathwayQuizResult) | undefined =
    undefined;
  @Input() criteriaScore: number = 0;
  @Input() embeddedStepsToReview: string[] | null | undefined = [];
  @Input() hasPassed: boolean = false;
  @Input() isUserCompliant: boolean | null = true;
  /** False when the call is out of compliance and the setting hides the other feedback. */
  @Input() showFeedback: boolean = true;
  @Input() complianceScoringResult: IComplianceScoring | undefined;
  @Input() complianceOnly: boolean = false;

  isExpandedd = true;
  calcTypeRatio5pt0min: string = AppConstants.CALL_RATIO_5PT_0MIN;

  constructor(protected localeService: LocaleService) {}

  get practiceScore(): number {
    return this.guidedResults?.practiceCall?.score ?? 0;
  }

  get callScoreCalcType(): string {
    return this.guidedResults?.callScoreCalcType ?? '';
  }

  get totalPositivePoints(): number {
    return (this.guidedResults?.questions || []).reduce(
      (sum, q) => sum + (q.practiceCallGuided.totalPositivePoints || 0),
      0,
    );
  }

  get totalPoints(): number {
    return (this.guidedResults?.questions || []).reduce(
      (sum, q) => sum + (q.practiceCallGuided.totalPoints || 0),
      0,
    );
  }

  get showMissedFacts(): boolean {
    return this.guidedResults?.showMissedFacts ?? false;
  }

  get hasMissedQuestions(): boolean {
    const questions = this.guidedResults?.questions || [];
    return questions.some(
      (question) =>
        (question.practiceCallGuided.negativeContext?.length || 0) > 0,
    );
  }

  get embeddedQuizReviewStepNames(): string[] {
    const backendStepNames = (this.embeddedStepsToReview ?? [])
      .map((stepName) => stepName?.trim())
      .filter((stepName): stepName is string => !!stepName);

    if (backendStepNames.length > 0) {
      return [...new Set(backendStepNames)];
    }

    return [];
  }

  get embeddedQuizReviewStepNamesText(): string {
    const names = this.embeddedQuizReviewStepNames;
    if (names.length === 0) {
      return '';
    }

    // Japanese lists use "、" with no conjunction: "A、B、C"
    const lang = this.localeService.getCurrentLanguage();
    if (lang === JAPANESE_LANGUAGE) {
      return formatList(names, lang);
    }

    if (names.length === 1) {
      return names[0];
    }

    if (names.length === 2) {
      return `${names[0]} and ${names[1]}`;
    }

    const allButLast = names.slice(0, -1).join(', ');
    const lastName = names[names.length - 1];
    return `${allButLast} and ${lastName}`;
  }

  get embeddedQuizStepWord(): string {
    return this.embeddedQuizReviewStepNames.length === 1 ? 'step' : 'steps';
  }

  get embeddedQuizStepVerb(): string {
    return this.embeddedQuizReviewStepNames.length === 1 ? 'was' : 'were';
  }

  get embeddedQuizAreaWord(): string {
    return this.embeddedQuizReviewStepNames.length === 1 ? 'area' : 'areas';
  }

  get embeddedQuizSummaryTranslationKey(): string {
    const hasStepNames = this.embeddedQuizReviewStepNames.length > 0;
    if (!hasStepNames) {
      return 'results.QUIZ_SUMMARY_BASE';
    }

    if (!this.hasPassed) {
      return 'results.QUIZ_SUMMARY_FAILED';
    }

    return this.hasMissedQuestions
      ? 'results.QUIZ_SUMMARY_PASSED_WITH_MISSES'
      : 'results.QUIZ_SUMMARY_BASE';
  }

}
